import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LinkItem } from "@/components/navigation/Nav";
import { isActive } from "./NavItem";
import {
  ARCHIVE_SEARCH_SHORTCUT_LABEL,
  useArchiveSearchDialog,
} from "@/components/customer/archive/archiveSearchDialogContext";

interface CollapsedNavItemProps {
  pathname: string;
  link: LinkItem;
}

export default function CollapsedNavItem({
  pathname,
  link,
}: CollapsedNavItemProps) {
  const { t } = useTranslation();
  const { open: openArchiveSearch } = useArchiveSearchDialog();
  const active = isActive(pathname, link.target);

  const staffColor = link.staffOnly && !active ? { color: "rgb(249 115 22)" } : undefined;

  const iconButton = (
    <div
      className={cn(
        buttonVariants({
          variant: active ? "secondary" : "ghost",
          size: "icon",
        }),
        "h-9 w-9 relative",
        {
          "opacity-50 cursor-not-allowed": link.disabled,
        },
      )}
      style={
        active
          ? {
              backgroundColor: "var(--color-secondary)",
              color: "var(--color-secondary-foreground)",
            }
          : staffColor
      }
    >
      <link.icon className="h-4 w-4" />
      {link.statusDot && (
        // Sits on the circle's edge (avatar-badge position), not a square
        // corner, so it doesn't get visually absorbed by the curve.
        <span className="absolute z-10 -bottom-0.5 -right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full">
          <span className="relative flex h-2 w-2 shrink-0">
            {link.online && (
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            )}
            {/* Solid, theme-independent color: a translucent/inherited-color dot
                washed out on the active item's dark-blue background before. */}
            <span
              className={cn(
                "relative inline-flex rounded-full h-2 w-2",
                link.online ? "bg-green-500" : "bg-slate-400",
              )}
            />
          </span>
        </span>
      )}
      <span className="sr-only">{t(link.title)}</span>
    </div>
  );

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>
        {link.disabled ? (
          <div>{iconButton}</div>
        ) : link.action === "archiveSearch" ? (
          <button type="button" onClick={() => openArchiveSearch()}>
            {iconButton}
          </button>
        ) : (
          <Link to={link.target}>{iconButton}</Link>
        )}
      </TooltipTrigger>
      <TooltipContent side="right" className="flex items-center gap-4">
        {link.disabled && link.disabledTooltip ? (
          <p>{t(link.disabledTooltip)}</p>
        ) : (
          <>
            {t(link.title)}
            {link.action === "archiveSearch" && (
              <span className="ml-auto text-muted-foreground">
                {ARCHIVE_SEARCH_SHORTCUT_LABEL}
              </span>
            )}
            {link.label && (
              <span className="ml-auto text-muted-foreground">{link.label}</span>
            )}
          </>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
